import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import { formatCopyNumber, toFiniteNumber } from "./masterConfigUtils";
import "./CopyTradingOpsPanels.css";

const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const emptyPag = { page: 1, limit: 20, total: 0, totalPages: 1 };

const useDebounced = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatNum = (value, digits = 4) => formatCopyNumber(value, digits);

const listFrom = (res) =>
  Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.data?.items)
    ? res.data.items
    : Array.isArray(res?.data?.rows)
    ? res.data.rows
    : [];

const pagFrom = (res, fallback) => {
  const pag = res?.pagination || {};
  return {
    page: pag.page || fallback.page,
    limit: pag.limit ?? fallback.limit,
    total: pag.total ?? listFrom(res).length,
    totalPages: pag.total_pages ?? pag.totalPages ?? 1,
  };
};

const boolLabel = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

const personLabel = (row, fallbackId) => {
  const name = [row?.first_name, row?.last_name].filter(Boolean).join(" ").trim();
  const ident = row?.uuid || row?.email_masked || row?.email || fallbackId;
  if (name && ident) return `${name} · ${ident}`;
  return name || ident || fallbackId || "—";
};

const FilterInput = ({ label, value, onChange, placeholder }) => (
  <label className="ct-ops-filter">
    {label}
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </label>
);

const Pagination = ({ pag, rowsLen, loading, onPage, onLimit }) => {
  if (loading && pag.total <= 0) return null;
  const start = rowsLen === 0 ? 0 : (pag.page - 1) * pag.limit + 1;
  const end = rowsLen === 0 ? 0 : (pag.page - 1) * pag.limit + rowsLen;
  return (
    <div className="ct-ops-pagination">
      <span>
        Showing {start}–{end}
        {pag.total ? <> of {pag.total}</> : null}
      </span>
      <div className="ct-ops-pagination__right">
        <label className="ct-ops-filter ct-ops-filter--inline">
          Rows
          <select
            value={pag.limit}
            onChange={(e) => onLimit(Number(e.target.value) || 20)}
          >
            {PAGE_LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        {pag.totalPages > 1 && (
          <div className="ct-ops-pagination__btns">
            <button type="button" disabled={pag.page <= 1} onClick={() => onPage(pag.page - 1)}>
              Prev
            </button>
            <button
              type="button"
              disabled={pag.page >= pag.totalPages}
              onClick={() => onPage(pag.page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyRow = ({ colSpan, loading, empty }) => (
  <tr>
    <td colSpan={colSpan} className="ct-ops-empty">
      {loading ? (
        <>
          <i className="fas fa-spinner fa-spin" /> Loading…
        </>
      ) : (
        empty
      )}
    </td>
  </tr>
);

const RiskPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const res = await AdminService.getCopyTradingRiskFlags();
    setLoading(false);
    if (res?.success) {
      setRows(listFrom(res));
    } else {
      setRows([]);
      setError(res?.message || "Failed to load risk flags.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const release = async (masterId) => {
    if (!masterId) return;
    setBusyId(masterId);
    const res = await AdminService.releaseMasterTotalStop(masterId);
    setBusyId("");
    if (res?.success) {
      toast.success(res.message || "Total-stop block released.");
      load();
    } else {
      toast.error(res?.message || "Failed to release total-stop.");
    }
  };

  return (
    <>
      <div className="ct-ops-toolbar">
        <p className="ct-ops-hint">
          Masters currently blocked from new opening mirrors. Closes still copy. Total-stop is sticky
          until an admin releases it; daily-loss and wallet-reserve clear on their own.
        </p>
        <button type="button" className="copy-trading-refresh-btn" onClick={load} disabled={loading}>
          <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
        </button>
      </div>
      {error ? <div className="copy-trading-error">{error}</div> : null}
      <div className="table-wrap copy-trading-table-wrap">
        <table className="data-table copy-trading-table">
          <thead>
            <tr>
              <th>Master</th>
              <th>Daily loss</th>
              <th>Total stop</th>
              <th>Wallet reserve</th>
              <th>Last evaluated</th>
              <th>Released</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? (
              <EmptyRow colSpan={7} loading={loading} empty="No masters are currently risk-blocked." />
            ) : (
              rows.map((row) => {
                const id = row.masterId || row._id || row.userId;
                const state = row.riskState || row;
                const settings = row.riskSettings || {};
                return (
                  <tr key={id}>
                    <td>
                      <code>{id || "—"}</code>
                      {row.displayName ? <div className="ct-ops-sub">{row.displayName}</div> : null}
                    </td>
                    <td>
                      <span className={`ct-ops-flag ${state.dailyLossBlocked ? "ct-ops-flag--on" : ""}`}>
                        {state.dailyLossBlocked ? "Blocked" : "Clear"}
                      </span>
                      <div className="ct-ops-sub">
                        {state.dailyLossReason || (settings.maximumDailyLossPercentage != null
                          ? `${settings.maximumDailyLossPercentage}%`
                          : "")}
                      </div>
                    </td>
                    <td>
                      <span className={`ct-ops-flag ${state.totalStopBlocked ? "ct-ops-flag--on" : ""}`}>
                        {state.totalStopBlocked ? "Blocked" : "Clear"}
                      </span>
                      <div className="ct-ops-sub">{state.totalStopReason || ""}</div>
                    </td>
                    <td>
                      <span className={`ct-ops-flag ${state.walletReserveBlocked ? "ct-ops-flag--on" : ""}`}>
                        {state.walletReserveBlocked ? "Blocked" : "Clear"}
                      </span>
                      <div className="ct-ops-sub">{state.walletReserveReason || ""}</div>
                    </td>
                    <td>{formatDate(state.lastEvaluatedAt)}</td>
                    <td>
                      {state.releasedAt ? (
                        <>
                          {formatDate(state.releasedAt)}
                          <div className="ct-ops-sub">{state.releasedBy || ""}</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="copy-trading-promote-btn"
                        disabled={!state.totalStopBlocked || busyId === id}
                        onClick={() => release(id)}
                        title="The only way a sticky total-stop clears"
                      >
                        {busyId === id ? (
                          <i className="fas fa-spinner fa-spin" />
                        ) : (
                          <i className="fas fa-unlock" />
                        )}{" "}
                        Release total-stop
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

const SettlementsPanel = () => {
  const [rows, setRows] = useState([]);
  const [pag, setPag] = useState(emptyPag);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [masterId, setMasterId] = useState("");
  const [followerId, setFollowerId] = useState("");
  const [masters, setMasters] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadMasters = async () => {
      const res = await AdminService.getMasterTraders({ role: "master", page: 1, limit: 100 });
      if (cancelled) return;
      const list = listFrom(res);
      setMasters(list);
      if (list.length === 1 && list[0]?._id) {
        setMasterId(String(list[0]._id));
      }
    };
    loadMasters();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!masterId) {
      setFollowers([]);
      setFollowerId("");
      return undefined;
    }
    let cancelled = false;
    const loadFollowers = async () => {
      const res = await AdminService.getMasterTraderFollowers(masterId, {
        page: 1,
        limit: 100,
      });
      if (cancelled) return;
      const list = listFrom(res);
      setFollowers(list);
      setFollowerId((current) => {
        if (current && list.some((f) => String(f.followerId || f.follower?._id) === current)) {
          return current;
        }
        return "";
      });
    };
    loadFollowers();
    return () => {
      cancelled = true;
    };
  }, [masterId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { page: pag.page, limit: pag.limit };
    if (masterId.trim()) params.masterId = masterId.trim();
    if (followerId.trim()) params.followerId = followerId.trim();
    const res = await AdminService.getCopyTradingSettlements(params);
    setLoading(false);
    if (res?.success) {
      setRows(listFrom(res));
      setPag((p) => pagFrom(res, p));
    } else {
      setRows([]);
      setError(res?.message || "Failed to load settlements.");
    }
  }, [pag.page, pag.limit, masterId, followerId]);

  useEffect(() => {
    load();
  }, [load]);

  const pageTotals = useMemo(() => {
    let collected = 0;
    let platform = 0;
    let credited = 0;
    rows.forEach((row) => {
      if (String(row.status || "").toUpperCase() !== "COMPLETED") return;
      collected += toFiniteNumber(row.totalCommission, 0);
      platform += toFiniteNumber(row.platformShare, 0);
      if (row.platformCredited) credited += toFiniteNumber(row.platformShare, 0);
    });
    return { collected, platform, credited };
  }, [rows]);

  const selectMaster = (id) => {
    setMasterId(id);
    setFollowerId("");
    setPag((p) => ({ ...p, page: 1 }));
  };

  const run = async (scope) => {
    const body = {};
    if (scope !== "all") {
      if (!masterId.trim()) {
        toast.error("Select a master trader first, then click Settle master.");
        return;
      }
      body.masterId = masterId.trim();
      if (scope === "pair") {
        if (!followerId.trim()) {
          toast.error("Select a follower first, then click Settle pair.");
          return;
        }
        body.followerId = followerId.trim();
      }
    }
    setRunning(true);
    const res = await AdminService.runCopyTradingSettlements(body);
    setRunning(false);
    if (res?.success) {
      toast.success(res.message || "Settlement run completed.");
      load();
    } else {
      toast.error(res?.message || "Settlement run failed.");
    }
  };

  return (
    <>
      <div className="ct-ops-tiles">
        <div className="ct-ops-tile">
          <div className="ct-ops-tile__label">Completed commission (this page)</div>
          <div className="ct-ops-tile__value">{formatNum(pageTotals.collected)}</div>
        </div>
        <div className="ct-ops-tile">
          <div className="ct-ops-tile__label">Platform share (this page)</div>
          <div className="ct-ops-tile__value">{formatNum(pageTotals.platform)}</div>
        </div>
        <div className="ct-ops-tile">
          <div className="ct-ops-tile__label">Platform credited (this page)</div>
          <div className="ct-ops-tile__value">{formatNum(pageTotals.credited)}</div>
          <div className="ct-ops-sub">Uncredited share means the platform wallet is not set yet.</div>
        </div>
      </div>
      <div className="ct-ops-toolbar">
        <div className="ct-ops-filters">
          <label className="ct-ops-filter">
            Master
            <select
              value={masterId}
              onChange={(e) => selectMaster(e.target.value)}
            >
              <option value="">All masters</option>
              {masters.map((row) => (
                <option key={row._id} value={row._id}>
                  {personLabel(row, row._id)}
                </option>
              ))}
            </select>
          </label>
          <label className="ct-ops-filter">
            Follower
            <select
              value={followerId}
              onChange={(e) => {
                setFollowerId(e.target.value);
                setPag((p) => ({ ...p, page: 1 }));
              }}
              disabled={!masterId}
            >
              <option value="">{masterId ? "All followers of this master" : "Select a master first"}</option>
              {followers.map((row) => {
                const id = row.followerId || row.follower?._id;
                return (
                  <option key={id} value={id}>
                    {personLabel(row.follower, id)}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
        <div className="ct-ops-actions">
          <button type="button" className="copy-trading-refresh-btn" onClick={load} disabled={loading || running}>
            <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            className="copy-trading-promote-btn"
            onClick={() => run("all")}
            disabled={running}
            title="Settle every active/paused follow this week"
          >
            {running ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-play" />} Run weekly job
          </button>
          <button
            type="button"
            className="copy-trading-config-btn"
            onClick={() => run("master")}
            disabled={running || !masterId}
            title={masterId ? "Settle every follower of the selected master" : "Select a master first"}
          >
            Settle master
          </button>
          <button
            type="button"
            className="copy-trading-config-btn"
            onClick={() => run("pair")}
            disabled={running || !masterId || !followerId}
            title={
              !masterId
                ? "Select a master first"
                : !followerId
                ? "Select a follower first"
                : "Settle this master–follower pair"
            }
          >
            Settle pair
          </button>
        </div>
      </div>
      <p className="ct-ops-hint">
        Pick a master (and optionally a follower), then settle. Weekly job needs no selection and
        settles everyone. Settlement moves real USDT; re-running the same ISO week is a no-op.
      </p>
      {error ? <div className="copy-trading-error">{error}</div> : null}
      <div className="table-wrap copy-trading-table-wrap">
        <table className="data-table copy-trading-table">
          <thead>
            <tr>
              <th>Settled</th>
              <th>Cycle</th>
              <th>Trigger</th>
              <th>Master</th>
              <th>Follower</th>
              <th>Profit</th>
              <th>Master / platform</th>
              <th>Total</th>
              <th>Status</th>
              <th>Moved</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? (
              <EmptyRow colSpan={10} loading={loading} empty="No settlement records yet." />
            ) : (
              rows.map((row) => (
                <tr
                  key={row._id || `${row.masterId}-${row.followerId}-${row.cycle}`}
                  className="ct-ops-row--click"
                  onClick={() => {
                    if (row.masterId) selectMaster(String(row.masterId));
                    if (row.followerId) setFollowerId(String(row.followerId));
                  }}
                  title="Use this pair for Settle master / Settle pair"
                >
                  <td>{formatDate(row.settledAt || row.createdAt)}</td>
                  <td>{row.cycle || "—"}</td>
                  <td>{row.trigger || "—"}</td>
                  <td><code>{row.masterId || "—"}</code></td>
                  <td><code>{row.followerId || "—"}</code></td>
                  <td>{formatNum(row.profitSubjectToShare)}</td>
                  <td>
                    {formatNum(row.masterShare)} / {formatNum(row.platformShare)}
                  </td>
                  <td>{formatNum(row.totalCommission)}</td>
                  <td>
                    <span className={`ct-ops-flag ct-ops-flag--${String(row.status || "").toLowerCase()}`}>
                      {row.status || "—"}
                    </span>
                  </td>
                  <td>
                    F {boolLabel(row.followerDebited)} · M {boolLabel(row.masterCredited)} · P{" "}
                    {boolLabel(row.platformCredited)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        pag={pag}
        rowsLen={rows.length}
        loading={loading}
        onPage={(page) => setPag((p) => ({ ...p, page }))}
        onLimit={(limit) => setPag((p) => ({ ...p, limit, page: 1 }))}
      />
    </>
  );
};

const AlertsPanel = () => {
  const [rows, setRows] = useState([]);
  const [pag, setPag] = useState(emptyPag);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [masterId, setMasterId] = useState("");
  const [acked, setAcked] = useState("false");
  const [busyId, setBusyId] = useState("");
  const debMasterId = useDebounced(masterId);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { page: pag.page, limit: pag.limit };
    if (debMasterId.trim()) params.masterId = debMasterId.trim();
    if (acked === "true" || acked === "false") params.acknowledged = acked;
    const res = await AdminService.getCopyTradingAlerts(params);
    setLoading(false);
    if (res?.success) {
      setRows(listFrom(res));
      setPag((p) => pagFrom(res, p));
    } else {
      setRows([]);
      setError(res?.message || "Failed to load alerts.");
    }
  }, [pag.page, pag.limit, debMasterId, acked]);

  useEffect(() => {
    load();
  }, [load]);

  const acknowledge = async (alertId) => {
    setBusyId(alertId);
    const res = await AdminService.acknowledgeCopyTradingAlert(alertId);
    setBusyId("");
    if (res?.success) {
      toast.success(res.message || "Alert acknowledged.");
      load();
    } else {
      toast.error(res?.message || "Failed to acknowledge alert.");
    }
  };

  return (
    <>
      <div className="ct-ops-toolbar">
        <div className="ct-ops-filters">
          <FilterInput label="Master ID" value={masterId} onChange={(v) => { setMasterId(v); setPag((p) => ({ ...p, page: 1 })); }} placeholder="optional" />
          <label className="ct-ops-filter">
            Acknowledged
            <select
              value={acked}
              onChange={(e) => {
                setAcked(e.target.value);
                setPag((p) => ({ ...p, page: 1 }));
              }}
            >
              <option value="false">Open</option>
              <option value="true">Acknowledged</option>
              <option value="">All</option>
            </select>
          </label>
        </div>
        <button type="button" className="copy-trading-refresh-btn" onClick={load} disabled={loading}>
          <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
        </button>
      </div>
      <p className="ct-ops-hint">
        Shared feed for risk-threshold breaches and surveillance flags. Surveillance never auto-bans.
      </p>
      {error ? <div className="copy-trading-error">{error}</div> : null}
      <div className="table-wrap copy-trading-table-wrap">
        <table className="data-table copy-trading-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Master</th>
              <th>Type</th>
              <th>Detail</th>
              <th>Ack</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? (
              <EmptyRow colSpan={6} loading={loading} empty="No alerts in this filter." />
            ) : (
              rows.map((row) => {
                const id = row._id || row.alertId;
                const type = row.type || row.kind || row.category || "—";
                const detail =
                  row.message ||
                  row.reason ||
                  (Array.isArray(row.breaches) ? row.breaches.join(", ") : "") ||
                  row.totalStopReason ||
                  row.dailyLossReason ||
                  "—";
                return (
                  <tr key={id}>
                    <td>{formatDate(row.createdAt || row.updatedAt)}</td>
                    <td><code>{row.masterId || "—"}</code></td>
                    <td>{type}</td>
                    <td>{detail}</td>
                    <td>{row.acknowledged ? "Yes" : "No"}</td>
                    <td>
                      <button
                        type="button"
                        className="copy-trading-promote-btn"
                        disabled={!!row.acknowledged || busyId === id}
                        onClick={() => acknowledge(id)}
                      >
                        {busyId === id ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}{" "}
                        Acknowledge
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        pag={pag}
        rowsLen={rows.length}
        loading={loading}
        onPage={(page) => setPag((p) => ({ ...p, page }))}
        onLimit={(limit) => setPag((p) => ({ ...p, limit, page: 1 }))}
      />
    </>
  );
};

const SurveillancePanel = () => {
  const [rows, setRows] = useState([]);
  const [pag, setPag] = useState(emptyPag);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [masterId, setMasterId] = useState("");
  const debMasterId = useDebounced(masterId);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { page: pag.page, limit: pag.limit };
    if (debMasterId.trim()) params.masterId = debMasterId.trim();
    const res = await AdminService.getCopyTradingSurveillance(params);
    setLoading(false);
    if (res?.success) {
      setRows(listFrom(res));
      setPag((p) => pagFrom(res, p));
    } else {
      setRows([]);
      setError(res?.message || "Failed to load surveillance.");
    }
  }, [pag.page, pag.limit, debMasterId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <div className="ct-ops-toolbar">
        <FilterInput label="Master ID" value={masterId} onChange={(v) => { setMasterId(v); setPag((p) => ({ ...p, page: 1 })); }} placeholder="optional" />
        <button type="button" className="copy-trading-refresh-btn" onClick={load} disabled={loading}>
          <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
        </button>
      </div>
      <p className="ct-ops-hint">
        Daily metrics. A breach (churn ≥ 50% or fee-to-profit ≥ 50%) creates an alert only — nothing is
        auto-paused.
      </p>
      {error ? <div className="copy-trading-error">{error}</div> : null}
      <div className="table-wrap copy-trading-table-wrap">
        <table className="data-table copy-trading-table">
          <thead>
            <tr>
              <th>Period</th>
              <th>Master</th>
              <th>Trades / day</th>
              <th>Fee / profit</th>
              <th>Churn</th>
              <th>Breaches</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? (
              <EmptyRow colSpan={6} loading={loading} empty="No surveillance rows yet." />
            ) : (
              rows.map((row, idx) => {
                const breaches = Array.isArray(row.breaches) ? row.breaches : [];
                return (
                  <tr key={`${row.masterId}-${row.periodDate}-${idx}`}>
                    <td>{row.periodDate ? formatDate(row.periodDate) : "—"}</td>
                    <td><code>{row.masterId || "—"}</code></td>
                    <td>{formatNum(row.tradesPerDay, 2)}</td>
                    <td>{row.followerFeeToProfitRatio == null ? "—" : formatNum(row.followerFeeToProfitRatio, 4)}</td>
                    <td>{row.churnScore == null ? "—" : formatNum(row.churnScore, 4)}</td>
                    <td>
                      {breaches.length ? (
                        <span className="ct-ops-flag ct-ops-flag--on">{breaches.join(", ")}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        pag={pag}
        rowsLen={rows.length}
        loading={loading}
        onPage={(page) => setPag((p) => ({ ...p, page }))}
        onLimit={(limit) => setPag((p) => ({ ...p, limit, page: 1 }))}
      />
    </>
  );
};

const ReconciliationPanel = () => {
  const [rows, setRows] = useState([]);
  const [pag, setPag] = useState(emptyPag);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [masterId, setMasterId] = useState("");
  const [resolved, setResolved] = useState("false");
  const [sinceMinutes, setSinceMinutes] = useState("30");
  const [running, setRunning] = useState(false);
  const debMasterId = useDebounced(masterId);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { page: pag.page, limit: pag.limit };
    if (debMasterId.trim()) params.masterId = debMasterId.trim();
    if (resolved === "true" || resolved === "false") params.resolved = resolved;
    const res = await AdminService.getCopyTradingReconciliation(params);
    setLoading(false);
    if (res?.success) {
      setRows(listFrom(res));
      setPag((p) => pagFrom(res, p));
    } else {
      setRows([]);
      setError(res?.message || "Failed to load reconciliation.");
    }
  }, [pag.page, pag.limit, debMasterId, resolved]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async () => {
    const minutes = Number(sinceMinutes);
    const body = {};
    if (Number.isFinite(minutes) && minutes > 0) body.sinceMinutes = Math.min(1440, minutes);
    setRunning(true);
    const res = await AdminService.runCopyTradingReconciliation(body);
    setRunning(false);
    if (res?.success) {
      toast.success(res.message || "Reconciliation run completed.");
      load();
    } else {
      toast.error(res?.message || "Reconciliation run failed.");
    }
  };

  return (
    <>
      <div className="ct-ops-toolbar">
        <div className="ct-ops-filters">
          <FilterInput label="Master ID" value={masterId} onChange={(v) => { setMasterId(v); setPag((p) => ({ ...p, page: 1 })); }} placeholder="optional" />
          <label className="ct-ops-filter">
            Resolved
            <select
              value={resolved}
              onChange={(e) => {
                setResolved(e.target.value);
                setPag((p) => ({ ...p, page: 1 }));
              }}
            >
              <option value="false">Open</option>
              <option value="true">Resolved</option>
              <option value="">All</option>
            </select>
          </label>
          <label className="ct-ops-filter">
            Since (minutes)
            <input
              type="number"
              min={1}
              max={1440}
              value={sinceMinutes}
              onChange={(e) => setSinceMinutes(e.target.value)}
            />
          </label>
        </div>
        <div className="ct-ops-actions">
          <button type="button" className="copy-trading-refresh-btn" onClick={load} disabled={loading || running}>
            <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
          </button>
          <button type="button" className="copy-trading-promote-btn" onClick={run} disabled={running}>
            {running ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-play" />} Run now
          </button>
        </div>
      </div>
      <p className="ct-ops-hint">
        Open master orders can be re-driven safely. Filled or cancelled orders are flagged only — never
        mirrored late at a stale price.
      </p>
      {error ? <div className="copy-trading-error">{error}</div> : null}
      <div className="table-wrap copy-trading-table-wrap">
        <table className="data-table copy-trading-table">
          <thead>
            <tr>
              <th>Master</th>
              <th>Follower</th>
              <th>Market</th>
              <th>Order</th>
              <th>Order status</th>
              <th>Action</th>
              <th>Reason</th>
              <th>Resolved</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? (
              <EmptyRow colSpan={8} loading={loading} empty="No reconciliation gaps in this filter." />
            ) : (
              rows.map((row, idx) => (
                <tr key={row._id || `${row.masterOrderId}-${row.followerId}-${idx}`}>
                  <td><code>{row.masterId || "—"}</code></td>
                  <td><code>{row.followerId || "—"}</code></td>
                  <td>{row.market || "—"}</td>
                  <td><code>{row.masterOrderId || "—"}</code></td>
                  <td>{row.masterOrderStatus || "—"}</td>
                  <td>
                    <span className={`ct-ops-flag ${row.action === "FLAGGED_LATE" ? "ct-ops-flag--on" : ""}`}>
                      {row.action || "—"}
                    </span>
                  </td>
                  <td>{row.reason || "—"}</td>
                  <td>
                    {row.resolved ? `Yes · ${formatDate(row.resolvedAt)}` : "No"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        pag={pag}
        rowsLen={rows.length}
        loading={loading}
        onPage={(page) => setPag((p) => ({ ...p, page }))}
        onLimit={(limit) => setPag((p) => ({ ...p, limit, page: 1 }))}
      />
    </>
  );
};

const SnapshotsPanel = () => {
  const [rows, setRows] = useState([]);
  const [pag, setPag] = useState(emptyPag);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scope, setScope] = useState("");
  const [masterId, setMasterId] = useState("");
  const [followerId, setFollowerId] = useState("");
  const debMasterId = useDebounced(masterId);
  const debFollowerId = useDebounced(followerId);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = { page: pag.page, limit: pag.limit };
    if (scope) params.scope = scope;
    if (debMasterId.trim()) params.masterId = debMasterId.trim();
    if (debFollowerId.trim()) params.followerId = debFollowerId.trim();
    const res = await AdminService.getCopyTradingSnapshots(params);
    setLoading(false);
    if (res?.success) {
      setRows(listFrom(res));
      setPag((p) => pagFrom(res, p));
    } else {
      setRows([]);
      setError(res?.message || "Failed to load snapshots.");
    }
  }, [pag.page, pag.limit, scope, debMasterId, debFollowerId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <div className="ct-ops-toolbar">
        <div className="ct-ops-filters">
          <label className="ct-ops-filter">
            Scope
            <select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setPag((p) => ({ ...p, page: 1 }));
              }}
            >
              <option value="">All</option>
              <option value="MASTER">MASTER</option>
              <option value="FOLLOWER_MASTER">FOLLOWER_MASTER</option>
            </select>
          </label>
          <FilterInput label="Master ID" value={masterId} onChange={(v) => { setMasterId(v); setPag((p) => ({ ...p, page: 1 })); }} placeholder="optional" />
          <FilterInput label="Follower ID" value={followerId} onChange={(v) => { setFollowerId(v); setPag((p) => ({ ...p, page: 1 })); }} placeholder="optional" />
        </div>
        <button type="button" className="copy-trading-refresh-btn" onClick={load} disabled={loading}>
          <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh
        </button>
      </div>
      <p className="ct-ops-hint">
        Immutable point-in-time values. Daily at 00:00 UTC, on demand, and at settlement. Never updated
        in place.
      </p>
      {error ? <div className="copy-trading-error">{error}</div> : null}
      <div className="table-wrap copy-trading-table-wrap">
        <table className="data-table copy-trading-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Scope</th>
              <th>Master</th>
              <th>Follower</th>
              <th>Value</th>
              <th>Trigger</th>
            </tr>
          </thead>
          <tbody>
            {loading || rows.length === 0 ? (
              <EmptyRow colSpan={6} loading={loading} empty="No snapshots yet." />
            ) : (
              rows.map((row, idx) => (
                <tr key={row._id || `${row.scope}-${row.createdAt}-${idx}`}>
                  <td>{formatDate(row.createdAt)}</td>
                  <td>{row.scope || "—"}</td>
                  <td><code>{row.masterId || "—"}</code></td>
                  <td><code>{row.followerId || "—"}</code></td>
                  <td>{formatNum(row.value)}</td>
                  <td>{row.trigger || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination
        pag={pag}
        rowsLen={rows.length}
        loading={loading}
        onPage={(page) => setPag((p) => ({ ...p, page }))}
        onLimit={(limit) => setPag((p) => ({ ...p, limit, page: 1 }))}
      />
    </>
  );
};

const CopyTradingOpsPanels = ({ section }) => {
  if (section === "risk") return <RiskPanel />;
  if (section === "settlements") return <SettlementsPanel />;
  if (section === "alerts") return <AlertsPanel />;
  if (section === "surveillance") return <SurveillancePanel />;
  if (section === "reconciliation") return <ReconciliationPanel />;
  if (section === "snapshots") return <SnapshotsPanel />;
  return null;
};

export default CopyTradingOpsPanels;
